
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useDynamicMeta, generateCertificateMeta } from '@/hooks/useDynamicMeta';

interface CertificateTemplateProps {
  userName: string;
  courseTitle: string;
  completionDate: string;
  quizAverage: number;
  certificateNumber: string;
  verificationHash?: string;
}

const CertificateTemplate: React.FC<CertificateTemplateProps> = ({
  userName,
  courseTitle,
  completionDate,
  quizAverage,
  certificateNumber,
  verificationHash
}) => {
  // Set dynamic meta tags for better social sharing
  const certificateMeta = generateCertificateMeta(userName, courseTitle, certificateNumber, verificationHash);
  useDynamicMeta(certificateMeta);
  return (
    <div className="certificate-template w-full max-w-4xl mx-auto bg-white shadow-2xl print:shadow-none">
      {/* Certificate Border */}
      <div className="border-8 border-blue-600 border-double min-h-[600px] p-8">
        
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white text-2xl font-bold">🏆</span>
            </div>
          </div>
          
          <h1 className="text-4xl font-bold text-blue-800 mb-2">
            CERTIFICATE OF COMPLETION
          </h1>
          
          <div className="w-32 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto mb-4"></div>
          
          <Badge className="bg-green-100 text-green-800 text-sm px-4 py-2">
            NeoCertify Verified
          </Badge>
        </div>

        {/* Main Content */}
        <div className="text-center mb-8">
          <p className="text-lg text-gray-600 mb-4">This is to certify that</p>
          
          <h2 className="text-3xl font-bold text-gray-800 mb-6 border-b-2 border-blue-200 pb-2 inline-block">
            {userName}
          </h2>
          
          <p className="text-lg text-gray-600 mb-4">has successfully completed</p>
          
          <h3 className="text-2xl font-semibold text-blue-700 mb-6">
            {courseTitle}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto mb-8">
            <Card className="p-4">
              <CardContent className="text-center p-0">
                <p className="text-sm text-gray-500 mb-1">Quiz Average</p>
                <p className="text-2xl font-bold text-green-600">{quizAverage.toFixed(1)}%</p>
              </CardContent>
            </Card>
            
            <Card className="p-4">
              <CardContent className="text-center p-0">
                <p className="text-sm text-gray-500 mb-1">Completion Date</p>
                <p className="text-lg font-semibold text-gray-700">{completionDate}</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Footer Section */}
        <div className="border-t-2 border-gray-200 pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-600">
            <div>
              <p className="font-semibold mb-1">Certificate Number:</p>
              <p className="font-mono text-xs">{certificateNumber}</p>
            </div>
            
            <div>
              <p className="font-semibold mb-1">Verification:</p>
              <p className="font-mono text-xs break-all">
                {verificationHash ? `verify.neoprismacademy.com/${verificationHash}` : 'Pending verification'}
              </p>
            </div>
          </div>
          
          <div className="text-center mt-6">
            <p className="text-xs text-gray-500">
              This certificate verifies successful completion of all course requirements including
              85% minimum score on each quiz and 80% overall course average.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateTemplate;
